import React, { useRef } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
);

const utahData = "/img/UTDT_Logo.png";
const kpi = "/img/KPI-DASH 2.png";
const bodex = "/img/bodexLogo.png";
const pdf = "/img/pdf1.png";

const EngTable3 = ({ data }) => {
  const pdfRef = useRef();

  const normalRows = data.filter((d) => d.average == null);
  const averageRow = data.find((d) => d.average != null);

  const sortedRows = [...normalRows].sort(
    (a, b) => (a.year || 0) - (b.year || 0),
  );

  // Chart Setup
  const chartData = {
    labels: sortedRows.map((item) => item.year),
    datasets: [
      {
        label: "Female Employment",
        data: sortedRows.map((item) => parseInt(item.female)),
        borderColor: "#EC4899",
        backgroundColor: "#EC489980",
        borderWidth: 3,
        tension: 0.4,
        pointBackgroundColor: "white",
        pointBorderColor: "#EC4899",
        pointBorderWidth: 2,
        pointRadius: 6,
        pointHoverRadius: 9,
        fill: true,
      },
      {
        label: "Average",
        data: sortedRows.map((_, i, arr) =>
          i === arr.length - 1 && averageRow
            ? parseInt(averageRow.average)
            : null,
        ),
        borderColor: "#10B981",
        backgroundColor: "rgba(16,185,129,0.4)",
        pointBorderWidth: 3,
        pointRadius: 8,
        pointHoverRadius: 10,
        fill: false,
        showLine: false,
      },
    ],
  };

  const options = {
    responsive: true,
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: "📈 Female Employment Trend with Average Reference",
        font: { size: 16, weight: "bold" },
      },
      tooltip: {
        callbacks: {
          label: (ctx) => `${ctx.raw.toLocaleString("en-US")} Employees`,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: "Employment Count",
          font: { size: 14, weight: "bold" },
        },
      },
      x: {
        title: {
          display: true,
          text: "Year",
          font: { size: 14, weight: "bold" },
        },
      },
    },
  };

  // Insight Calculations
  const firstValue = sortedRows[0].female;
  const lastValue = sortedRows[sortedRows.length - 1].female;
  const growthPercent = (((lastValue - firstValue) / firstValue) * 100).toFixed(
    1,
  );

  const peak = sortedRows.reduce(
    (max, d) => (d.female > max.female ? d : max),
    sortedRows[0],
  );

 const downloadPDF = () => {
   const input = pdfRef.current;
   html2canvas(input, {
     scale: 2,
     useCORS: true,
     backgroundColor: "#ffffff",
     scrollY: -window.scrollY,
   }).then((canvas) => {
     const imgData = canvas.toDataURL("image/png");
     const pdf = new jsPDF("p", "mm", "a4");
     const pageWidth = pdf.internal.pageSize.getWidth();
     const pageHeight = pdf.internal.pageSize.getHeight();
     const imgWidth = pageWidth - 10;
     const imgHeight = (canvas.height * imgWidth) / canvas.width;

     let heightLeft = imgHeight;
     let position = 0;
     let pageNumber = 1;

     const addHeader = () => {
       const logoWidth = 20; // ideal header width
       const logoHeight = 20; // adjust ratio if needed

       pdf.addImage(utahData, "PNG", 70, 10, logoWidth, logoHeight);
       pdf.setFontSize(26);
       pdf.text("UTahData.org", 95, 22);
       pdf.link(50, 15, 60, 10, { url: "https://utahdata.org" });
       pdf.line(10, 35, 200, 35);
     };

     const addFooter = (pageNumber) => {
       pdf.setFontSize(9);
       const footerY = pageHeight - 10;
       pdf.line(10, footerY - 5, pageWidth - 10, footerY - 5);
       pdf.text(
         `Downloaded on: ${new Date().toLocaleDateString()}`,
         10,
         footerY,
       );
       pdf.text(
         `Page ${pageNumber}`,
         pageWidth - 10,
         footerY,
         null,
         null,
         "right",
       );
     };

     // First page
     addHeader();
     pdf.addImage(imgData, "PNG", 5, 32, imgWidth, imgHeight);
     heightLeft -= pageHeight - 32;

     // Add more pages if needed
     while (heightLeft > 0) {
       pdf.addPage();
       pageNumber++;
       addHeader();
       position = heightLeft - imgHeight;
       pdf.addImage(imgData, "PNG", 5, position + 32, imgWidth, imgHeight);
       heightLeft -= pageHeight - 32;
       addFooter(pageNumber);
     }

     // Final footer
     // ===== FINAL FOOTER SAFE AREA =====

     const footerTopY = pageHeight - 40; // start footer 40mm from bottom
     const footerContentY = pageHeight - 28; // logos row
     const footerBottomTextY = pageHeight - 10; // bottom text row

     // Top Divider Line
     pdf.line(10, footerTopY, pageWidth - 10, footerTopY);

     // UtahData logo
     const utahWidth = 18;
     const utahHeight = 18;
     pdf.addImage(
       utahData,
       "PNG",
       20,
       footerContentY - 8,
       utahWidth,
       utahHeight,
     );

     // Built by
     pdf.setFontSize(10);
     pdf.text("Built by", 45, footerContentY);

     // BODEX logo
     const bodexWidth = 36;
     const bodexHeight = bodexWidth * 0.3;
     pdf.addImage(
       bodex,
       "PNG",
       60,
       footerContentY - 6,
       bodexWidth,
       bodexHeight,
     );

     // Using text
     pdf.text("using", 102, footerContentY);

     // KPI logo
     pdf.addImage(kpi, "PNG", 118, footerContentY - 8, 16, 16);

     // Version
     pdf.text("20250916.v3.198", 140, footerContentY);

     // Bottom Info Line
     pdf.setFontSize(8);
     pdf.text(`From: ${window.location.href}`, 10, footerBottomTextY);
     pdf.text(
       `Downloaded on: ${new Date().toLocaleDateString()}`,
       pageWidth - 10,
       footerBottomTextY,
       null,
       null,
       "right",
     );

     pdf.save("female_employment_trend.pdf");
   });
 };

  return (
    <>
      <div ref={pdfRef} className="p-6 bg-white rounded-xl shadow-lg">
        <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">
          Female Employment Trend (With Average Benchmark)
        </h2>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 text-center">
          <div className="p-4 bg-blue-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">Overall Growth</p>
            <p className="text-xl font-bold text-blue-800">+{growthPercent}%</p>
          </div>
          <div className="p-4 bg-yellow-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">Peak Employment</p>
            <p className="text-xl font-bold text-yellow-800">
              {peak.year.toLocaleString("en-US")}
            </p>
          </div>
          <div className="p-4 bg-green-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">Avg Benchmark</p>
            <p className="text-xl font-bold text-green-800">
              {averageRow
                ? Number(averageRow.average).toLocaleString("en-US")
                : "N/A"}
            </p>
          </div>
        </div>

        {/* Table + Chart */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Table */}

          <div className="flex flex-col justify-center">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-center shadow-md rounded-lg overflow-hidden">
                <thead className="bg-gray-800 text-white">
                  <tr>
                    <th className="p-2 text-left">Year</th>
                    <th className="p-2" style={{ textAlign: "right" }}>
                      Female
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedRows.map((row) => (
                    <tr
                      key={row.year}
                      className={`${
                        row.year === peak.year
                          ? "bg-green-100 font-bold"
                          : "hover:bg-gray-100"
                      }`}
                    >
                      <td className="p-2 text-left">{row.year}</td>
                      <td
                        className="p-2"
                        style={{
                          textAlign: "right",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {Number(row.female).toLocaleString("en-US")}
                      </td>
                    </tr>
                  ))}
                  {averageRow && (
                    <tr className="bg-green-200 font-bold">
                      <td className="p-2 text-left">Average</td>
                      <td
                        className="p-2"
                        style={{
                          textAlign: "right",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {Number(averageRow.average).toLocaleString("en-US")}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Chart */}
          <div className="flex flex-col w-full overflow-x-auto bg-white rounded-lg shadow-md">
            <div className="min-w-[400px] w-full h-96 p-4">
              <Line data={chartData} options={options} />
            </div>
          </div>
        </div>
      </div>

      {/* Download Button */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          Download as PDF
          <img className="w-[1.5rem]" src={pdf} alt="pdf-icon" />
        </button>
      </div>
    </>
  );
};

export default EngTable3;
